<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep, WritableDeep } from 'type-fest';
import type { TableFoldProps as DashboardTableFoldProps } from '@/components/tableFolds/DashboardTableFold.vue';
import type { HeadersData } from '@/composables/useTable';

import { computed, onUnmounted, ref, watch } from 'vue';

import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import JobAttributeCollection from '@/components/collections/JobAttributeCollection.vue';
import SmallTrafficLightDiagram from '@/components/diagrams/SmallTrafficLightDiagram.vue';
import OpenJobEditIconLink from '@/components/iconLinks/OpenJobEditIconLink.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import DashboardTableFold from '@/components/tableFolds/DashboardTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import KTooltip from '@/components/tooltips/KTooltip.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';
import { fetchGetJobPrintoutFile } from '@/helpers/fetchers';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  uniqueKey: string;
  jobId: number;
  jobDate: string;
  isSplitToBase?: boolean;
  splitToBase?: string;
  isFutile?: boolean;
  hasAttachment?: boolean;
  isPriority?: boolean;
  isRfc?: boolean;
  customerCode?: string;
  reference1?: string;
  ref1Prompt?: string;
  suburb?: string;
  stopName?: string;
  state?: string;
  driver?: string;
  attributes: string[];
  status?: string;
  fold: DashboardTableFoldProps;
  trafficlight: object[];
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
  isStaff: false,
});

const emit = defineEmits<{
  editClick: [jobId: number, jobDate: string];
}>();

const openFoldKey = ref('');
const isRenderCounter = ref(0);

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const _sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const sortedData = ref<DataItem[]>([]);

const isUnmounted = ref(false);
const renderedSortedData = ref<ReadonlyDeep<DataItem[]>>([]);

async function renderSortedData(
  newSortedData: ReadonlyDeep<DataItem[]>,
): Promise<void> {
  sortedData.value = [];
  await new Promise((resolve) => setTimeout(resolve, 50));
  renderedSortedData.value = newSortedData;
  for (let index = 0; index < newSortedData.length; index++) {
    if (isUnmounted.value || renderedSortedData.value !== newSortedData) return;
    sortedData.value[index] = newSortedData[index] as WritableDeep<DataItem>;
    if (index < 3) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }
  renderedSortedData.value = [];
}

watch(
  _sortedData,
  async (newValues, oldValues) => {
    isRenderCounter.value++;
    await renderSortedData(newValues).finally(() => {
      isRenderCounter.value--;
    });
  },
  { immediate: true },
);

onUnmounted(() => {
  isUnmounted.value = true;
});

const headersData = computed<HeadersData>(function () {
  if (props.isStaff) {
    return [
      {
        value: 'jobId',
        title: 'Job ID',
        colspan: 2,
      },
      {
        value: 'customerCode',
        title: 'Customer code',
      },
      {
        value: 'reference1',
        title: 'Reference 1',
      },
      {
        value: 'suburb',
        title: 'Suburb',
      },
      {
        value: 'stopName',
        title: 'Stop name',
      },
      {
        value: 'state',
        title: 'State',
      },
      {
        value: 'driver',
        title: 'Driver',
      },
      {
        value: 'attributes',
        title: 'Attributes',
      },
      {
        value: 'status',
        title: 'Status',
      },
    ];
  } else {
    return [
      {
        value: 'jobId',
        title: 'Job ID',
        colspan: 2,
      },
      {
        value: 'reference1',
        title: 'Reference 1',
      },
      {
        value: 'suburb',
        title: 'Suburb',
      },
      {
        value: 'stopName',
        title: 'Stop name',
      },
      {
        value: 'state',
        title: 'State',
      },
      {
        value: 'attributes',
        title: 'Attributes',
      },
      {
        value: 'status',
        title: 'Status',
      },
    ];
  }
});

const foldColspan = computed(() => {
  return headersData.value.length + 2;
});

function handleClick(dataItemUniqueKey: string): void {
  if (openFoldKey.value === dataItemUniqueKey) {
    openFoldKey.value = '';
  } else {
    openFoldKey.value = dataItemUniqueKey;
  }
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.folded,
      tableStyles.hovered,
      tableStyles.bordered,
      $style.dashboardTable,
    ]"
    data-testid="dashboardTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
        <th :class="$style.diagramTh">
          <div>
            <div>Stop 1</div>
            <div>Stop 2</div>
            <div>Completed</div>
          </div>
        </th>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-if="props.isStaff">
        <template v-for="dataItem in sortedData" :key="dataItem.uniqueKey">
          <tr
            :class="[
              tableStyles.foldTitle,
              { [$style.isRfc]: dataItem.isRfc },
              { [$style.isPriority]: dataItem.isPriority },
              { [tableStyles.foldOpen]: openFoldKey === dataItem.uniqueKey },
            ]"
          >
            <td :class="$style.foldTd">
              <v-lazy>
                <TableFoldBtn
                  :class="tableStyles.foldBtn"
                  :is-open="openFoldKey === dataItem.uniqueKey"
                  @click="handleClick(dataItem.uniqueKey)"
                />
              </v-lazy>
            </td>
            <td :class="$style.jobIdTd">
              <div :class="$style.jobIdTdInner">
                <v-lazy>
                  <template v-if="dataItem.hasAttachment">
                    <KTooltip location="top" :offset="7">
                      Has attachments
                      <template #activator="{ props: tooltipProps }">
                        <v-icon
                          v-bind="tooltipProps"
                          :class="$style.attachmentsIcon"
                          icon="mdi-attachment"
                        ></v-icon>
                      </template>
                    </KTooltip>
                  </template>
                </v-lazy>
                <JobIdAndIconLinkUnit
                  :job-id="dataItem.jobId"
                  :job-date="dataItem.jobDate"
                /><v-lazy class="d-inline"
                  ><OpenJobEditIconLink
                    :class="$style.editBtn"
                    @click="emit('editClick', dataItem.jobId, dataItem.jobDate)"
                  />
                </v-lazy>
              </div>
            </td>
            <td :class="$style.customerCodeTd">
              {{ dataItem.customerCode }}
            </td>
            <td :class="$style.reference1Td">
              <v-lazy>
                <span>
                  <KParentTooltip
                    v-if="dataItem.ref1Prompt"
                    location="bottom"
                    :offset="3"
                  >
                    {{ dataItem.ref1Prompt }}
                  </KParentTooltip>
                  {{ dataItem.reference1 }}
                </span>
              </v-lazy>
            </td>
            <td :class="$style.suburbTd">
              {{ dataItem.suburb }}
            </td>
            <td :class="$style.stopNameTd">
              {{ dataItem.stopName }}
            </td>
            <td :class="$style.stateTd">
              {{ dataItem.state }}
            </td>
            <td :class="$style.driverTd">
              {{ dataItem.driver }}
            </td>
            <td :class="$style.attributesTd">
              <v-lazy>
                <JobAttributeCollection :attributes="dataItem.attributes" />
              </v-lazy>
            </td>
            <td :class="$style.statusTd">
              {{ dataItem.status }}
            </td>
            <td :class="$style.diagramTd">
              <v-lazy>
                <SmallTrafficLightDiagram
                  :class="$style.diagram"
                  :statuses="dataItem.trafficlight"
                />
              </v-lazy>
            </td>
          </tr>
          <tr
            :class="[
              tableStyles.foldContent,
              { [tableStyles.foldOpen]: openFoldKey === dataItem.uniqueKey },
            ]"
          >
            <td :colspan="foldColspan">
              <div v-if="openFoldKey === dataItem.uniqueKey">
                <DashboardTableFold
                  v-bind="dataItem.fold"
                  :download-shipping-label="
                    () =>
                      fetchGetJobPrintoutFile({
                        job_id: dataItem.jobId,
                        job_date: dataItem.jobDate,
                      })
                  "
                />
              </div>
            </td>
          </tr>
        </template>
      </template>
      <template v-else>
        <template v-for="dataItem in sortedData" :key="dataItem.uniqueKey">
          <tr
            :class="[
              tableStyles.foldTitle,
              { [$style.isRfc]: dataItem.isRfc },
              { [$style.isPriority]: dataItem.isPriority },
              { [tableStyles.foldOpen]: openFoldKey === dataItem.uniqueKey },
            ]"
          >
            <td :class="$style.foldTd">
              <v-lazy>
                <TableFoldBtn
                  :class="tableStyles.foldBtn"
                  :is-open="openFoldKey === dataItem.uniqueKey"
                  @click="handleClick(dataItem.uniqueKey)"
                />
              </v-lazy>
            </td>
            <td :class="$style.jobIdTd">
              <div :class="$style.jobIdTdInner">
                <v-lazy>
                  <template v-if="dataItem.hasAttachment">
                    <KTooltip location="top" :offset="7">
                      Has attachments
                      <template #activator="{ props: tooltipProps }">
                        <v-icon
                          v-bind="tooltipProps"
                          :class="$style.attachmentsIcon"
                          icon="mdi-attachment"
                        ></v-icon>
                      </template>
                    </KTooltip>
                  </template>
                </v-lazy>
                <JobIdAndIconLinkUnit
                  :job-id="dataItem.jobId"
                  :job-date="dataItem.jobDate"
                />
              </div>
            </td>
            <td :class="$style.reference1Td">
              <v-lazy>
                <span>
                  <KParentTooltip
                    v-if="dataItem.ref1Prompt"
                    location="bottom"
                    :offset="3"
                  >
                    {{ dataItem.ref1Prompt }}
                  </KParentTooltip>
                  {{ dataItem.reference1 }}
                </span>
              </v-lazy>
            </td>
            <td :class="$style.suburbTd">
              {{ dataItem.suburb }}
            </td>
            <td :class="$style.stopNameTd">
              {{ dataItem.stopName }}
            </td>
            <td :class="$style.stateTd">
              {{ dataItem.state }}
            </td>
            <td :class="$style.attributesTd">
              <v-lazy>
                <JobAttributeCollection :attributes="dataItem.attributes" />
              </v-lazy>
            </td>
            <td :class="$style.statusTd">
              {{ dataItem.status }}
            </td>
            <td :class="$style.diagramTd">
              <v-lazy>
                <SmallTrafficLightDiagram
                  :class="$style.diagram"
                  :statuses="dataItem.trafficlight"
                />
              </v-lazy>
            </td>
          </tr>
          <tr
            :class="[
              tableStyles.foldContent,
              { [tableStyles.foldOpen]: openFoldKey === dataItem.uniqueKey },
            ]"
          >
            <td :colspan="foldColspan">
              <div v-if="openFoldKey === dataItem.uniqueKey">
                <DashboardTableFold v-bind="dataItem.fold" />
              </div>
            </td>
          </tr>
        </template>
      </template>
    </tbody>
    <tr v-if="isRenderCounter && !props.loading">
      <td :colspan="foldColspan" class="!h-24 p-0">
        <TableLoader />
      </td>
    </tr>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:color';
@use '@/styles/brand-primary' as bp;
@use '@/styles/colors';
@use '@/styles/variables';
@use '@/styles/table.module.scss' as tableStyles;

.dashboardTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
  .jobIdTh {
    min-width: 130px;
    width: 130px;
  }
  .customerCodeTh {
    @include tableStyles.column-extra-wide;
  }
  .reference1Th {
    @include tableStyles.column-wide;
  }
  .suburbTh {
    @include tableStyles.column-flexible;
  }
  .stopNameTh {
    @include tableStyles.column-flexible;
  }
  .stateTh {
    @include tableStyles.column-small;
  }
  .driverTh {
    @include tableStyles.column-small;
  }
  .attributesTh {
    @include tableStyles.column-wide;
  }
  .statusTh {
    @include tableStyles.column-extra-wide;
  }
  .diagramTh {
    min-width: 235px;
    width: 235px;

    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
  .foldTd {
    min-width: 40px;
    width: 40px;
    white-space: nowrap;
  }
  .jobIdTd {
  }
    .jobIdTdInner {
      position: relative;
      white-space: nowrap;
    }
    .editBtn {
      margin-top: -8.5px;
      margin-bottom: -7px;
      vertical-align: top;
    }
      .attachmentsIcon {
        opacity: 0.7;
        color: colors.$color-white;
        background-color: bp.$color-brand-400;
        border-radius: 50%;
        font-size: 14px;
        position: absolute;
        right: -4px;
      }
  .customerCodeTd {
  }
  .reference1Td {
  }
  .suburbTd {
  }
  .stopNameTd {
  }
  .stateTd {
  }
  .driverTd {
  }
  .attributesTd {
  }
  .statusTd {
  }
  .diagramTd {
  }
    .diagram {
      margin: -13px -7px -27px -7px;
    }

tr.isRfc {
  & > td {
    background-color: colors.$color-orange-100 !important;
  }
  &:hover > td {
    background-color: color.mix(colors.$color-orange-100, color.adjust(colors.$color-blue-50, $alpha: -0.4)) !important;
  }
}

tr.isPriority {
  & > td {
    background-color: colors.$color-red-100 !important;
  }
  &:hover > td {
    background-color: color.mix(colors.$color-red-100, color.adjust(colors.$color-blue-50, $alpha: -0.4)) !important;
  }
}
</style>
